/**
 * The gate between what a client *asks for* and what the world *does*.
 *
 * Every intent is re-derived from authoritative state before it is applied. The
 * intent carries a request and nothing else: no cost, no expected outcome, no
 * claim about what a hex currently is. docs/04 is blunt about this and it is
 * cheap to honour now — the alternative is discovering in Phase 10 that half the
 * handlers trusted a field a hostile client controls.
 *
 * Legality itself is never decided here. `checkClaim` and `checkBuild` live in
 * `shared/src/rules.ts` so the client can grey out an unaffordable hex using the
 * exact function the server will authorise it with. This module's job is to
 * assemble the context those functions need from real state, and to perform the
 * mutation if they say yes.
 *
 * Nothing in here throws. A malformed intent inside a tick must be a rejection,
 * not a crashed simulation.
 */
import { BUILDINGS, FORTIFY_DURATION_SECONDS, GOD_TOOLS, checkBuild, checkClaim, checkGodTool, clamp01, subtractBundles, } from "kngdm-shared";
import { TICK_RATE_HZ } from "../tick.js";
import { isValidSurgeTarget } from "./borders.js";
import { getHex, isFortified, markDirty, neighbourOwnerMap } from "./state.js";
import { calmEventsAt, isUnderEvent, provokeEventAt } from "./worldEvents.js";
const ACCEPTED = { accepted: true, reason: null };
function reject(reason) {
    return { accepted: false, reason };
}
/**
 * Validate and apply one intent. Call only from tick-time code.
 *
 * Rejection reasons are the rule modules' own codes where they have one
 * (`not-adjacent`, `wrong-terrain`, …) so the client can map a refusal to a
 * message without the server inventing a second vocabulary.
 */
export function applyIntent(state, kingdomId, intent) {
    const kingdom = state.kingdoms.get(kingdomId);
    if (kingdom === undefined)
        return reject("unknown-kingdom");
    if (kingdom.eliminated)
        return reject("eliminated");
    // Intents queued before the final tick can still arrive after it; they must
    // not be able to rewrite a finished session's standings.
    if (state.ended)
        return reject("session-ended");
    switch (intent.type) {
        case "claimHex":
            return applyClaim(state, kingdomId, intent.hex);
        case "placeBuilding":
            return applyBuild(state, kingdomId, intent.buildingType, intent.hex);
        case "setWorkerTroopSplit": {
            if (!Number.isFinite(intent.workerRatio))
                return reject("invalid-ratio");
            kingdom.workerRatio = clamp01(intent.workerRatio);
            return ACCEPTED;
        }
        case "surge": {
            if (!isValidSurgeTarget(state, kingdomId, intent.borderSegmentId)) {
                return reject("invalid-segment");
            }
            // A Set makes repeat surges on one segment idempotent rather than
            // stacking, so a client that spams during a drag can't multiply its weight.
            kingdom.surges.add(intent.borderSegmentId);
            return ACCEPTED;
        }
        case "clearSurge": {
            // Clearing an order you hold always works, even if the segment has since
            // stopped being a border — otherwise losing the hex under a surge would
            // strand the order until the simulation's own pruning caught it.
            if (kingdom.surges.delete(intent.borderSegmentId))
                return ACCEPTED;
            if (!isValidSurgeTarget(state, kingdomId, intent.borderSegmentId)) {
                return reject("invalid-segment");
            }
            return ACCEPTED;
        }
        case "useGodTool":
            return applyGodTool(state, kingdomId, intent.tool, intent.hex);
        case "moveUnit":
        case "tradeOffer":
            // Declared in the protocol (docs/04), built in the named-unit and trade
            // phases. Rejected explicitly so a half-wired feature fails loudly.
            return reject("not-implemented");
        default:
            return rejectUnknown(intent);
    }
}
/**
 * The `never` parameter makes the switch above exhaustive at compile time, while
 * still returning something sane for a runtime payload that isn't in the union
 * at all — which is exactly what a WebSocket will hand us in Phase 10.
 */
function rejectUnknown(intent) {
    const type = intent.type;
    return reject(`unknown-intent:${String(type)}`);
}
function applyClaim(state, kingdomId, hex) {
    const kingdom = state.kingdoms.get(kingdomId);
    if (kingdom === undefined)
        return reject("unknown-kingdom");
    const record = getHex(state, hex);
    const check = checkClaim(hex, {
        biome: record?.biome ?? null,
        owner: record?.owner ?? null,
        neighbourOwners: neighbourOwnerMap(state, hex),
        claimantId: kingdomId,
        claimantResources: kingdom.resources,
        // Read from state, never from the intent: the cost curve is the main
        // anti-snowball lever and a client-supplied territory count would defeat it.
        claimantTerritoryCount: kingdom.hexes.size,
        claimantEliminated: kingdom.eliminated,
    });
    if (!check.allowed || record === undefined)
        return reject(check.reason ?? "rejected");
    kingdom.resources = debit(kingdom.resources, check.cost);
    record.owner = kingdomId;
    kingdom.hexes.add(record.key);
    // The capture meter tracks progress against a hex's owner, so it is stale the
    // moment the hex has one. Buying contested ground out from under a slow push
    // is the fast path doing what it is for.
    record.capture = 0;
    record.captureBy = null;
    markDirty(state, record);
    return ACCEPTED;
}
/**
 * Spend Aether on a god tool.
 *
 * Legality is `checkGodTool` in the shared rules, so the client greys out what
 * the server would refuse using the identical function. What is left here is the
 * effect, and each one is a single mutation — which is why four tools share one
 * handler rather than four.
 */
function applyGodTool(state, kingdomId, tool, hex) {
    const kingdom = state.kingdoms.get(kingdomId);
    if (kingdom === undefined)
        return reject("unknown-kingdom");
    if (!GOD_TOOLS.includes(tool))
        return reject("unknown-tool");
    const record = getHex(state, hex);
    const check = checkGodTool(tool, {
        biome: record?.biome ?? null,
        owner: record?.owner ?? null,
        userId: kingdomId,
        userResources: kingdom.resources,
        underEvent: isUnderEvent(state, hex),
        fortified: record === undefined ? false : isFortified(record, state.tick),
    });
    if (!check.allowed || record === undefined)
        return reject(check.reason ?? "rejected");
    switch (tool) {
        case "terraform":
            // Plains specifically, not a chosen biome: it keeps the tool to one click
            // and one meaning — turn bad ground into farmland. A biome picker is the
            // obvious follow-up and needs UI this does not.
            record.biome = "plains";
            break;
        case "fortify":
            record.fortifiedUntilTick =
                state.tick + Math.max(1, Math.round(FORTIFY_DURATION_SECONDS * TICK_RATE_HZ));
            break;
        case "calm":
            // Re-checked rather than trusted from `check`: the check ran against the
            // same tick, but this is the mutation and it should not assume.
            if (calmEventsAt(state, hex) === 0)
                return reject("nothing-to-calm");
            break;
        case "provoke":
            if (provokeEventAt(state, hex) === null)
                return reject("rejected");
            break;
        /* c8 ignore next 2 */
        default:
            return reject("unknown-tool");
    }
    kingdom.resources = debit(kingdom.resources, check.cost);
    markDirty(state, record);
    return ACCEPTED;
}
function applyBuild(state, kingdomId, buildingType, hex) {
    const kingdom = state.kingdoms.get(kingdomId);
    if (kingdom === undefined)
        return reject("unknown-kingdom");
    // The union says this is a known building; the wire says whatever it likes.
    if (!Object.prototype.hasOwnProperty.call(BUILDINGS, buildingType)) {
        return reject("unknown-building");
    }
    const definition = BUILDINGS[buildingType];
    const record = getHex(state, hex);
    const check = checkBuild(definition.type, {
        biome: record?.biome ?? null,
        owner: record?.owner ?? null,
        existingBuilding: record?.building ?? null,
        builderId: kingdomId,
        builderResources: kingdom.resources,
    });
    if (!check.allowed || record === undefined)
        return reject(check.reason ?? "rejected");
    kingdom.resources = debit(kingdom.resources, check.cost);
    record.building = definition.type;
    markDirty(state, record);
    return ACCEPTED;
}
/**
 * Pay a cost. Floating-point accrual can leave a balance a hair under a cost it
 * was checked against, so the result is floored at zero rather than allowed to
 * go very slightly negative and poison every later comparison.
 */
function debit(resources, cost) {
    const next = subtractBundles(resources, cost);
    return {
        food: Math.max(0, next.food),
        timber: Math.max(0, next.timber),
        ore: Math.max(0, next.ore),
        gold: Math.max(0, next.gold),
        aether: Math.max(0, next.aether),
    };
}
//# sourceMappingURL=intents.js.map